export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  data?: Record<string, unknown> | FormData
}

function csrfToken(): string | null {
  const token = document.querySelector('meta[name="csrf-token"]')
  return token ? token.getAttribute('content') : null
}

/**
 * POST to an endpoint that responds with a file, via a real form submission.
 *
 * File downloads cannot go through Inertia's router or fetch: the router
 * expects an Inertia response and errors on a CSV body, and a fetch response
 * never reaches the browser's download handling. A native form submit is the
 * only thing that makes the browser save the file. Blank values are skipped so
 * unset filters don't reach the server as empty strings.
 */
export function submitDownloadForm(action: string, fields: Record<string, string | null | undefined>): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = action
  form.style.display = 'none'

  const token = csrfToken()
  if (token) appendHiddenInput(form, 'authenticity_token', token)

  Object.entries(fields).forEach(([name, value]) => {
    if (value) appendHiddenInput(form, name, value)
  })

  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}

function appendHiddenInput(form: HTMLFormElement, name: string, value: string): void {
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = name
  input.value = value
  form.appendChild(input)
}

export async function requestJson<T = Record<string, unknown>>(url: string, options: RequestOptions = {}): Promise<T | null> {
  const method = options.method || 'GET'
  const headers: HeadersInit = {
    Accept: 'application/json',
  }

  const token = csrfToken()
  if (token) headers['X-CSRF-Token'] = token

  let body: BodyInit | undefined

  if (options.data instanceof FormData) {
    body = options.data
  } else if (options.data) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.data)
  }

  const response = await fetch(url, {
    method,
    headers,
    body,
    credentials: 'same-origin',
  })

  if (response.status === 204) return null

  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json') ? await response.json() : await response.text()

  if (!response.ok) {
    const message = typeof payload === 'string' ? payload : payload?.error || payload?.message || `Request failed with ${response.status}`
    throw new Error(message)
  }

  return payload as T
}
